import React, { useState, useEffect, useCallback } from "react";
import { useToaster } from '../../contexts/ToasterContext';
import { Card, CardContent } from "../../components/ui/card.jsx";
import { RefreshCcw, Shield, AlertTriangle, Cloud } from "lucide-react";
import { Button } from "../../components/ui/button.jsx";
import IamOnboarding from "./IamOnboarding";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog.jsx";
import cloudInfraScanSchedules from "../../rawData/cloudInfraScanSchedules.json";

// Provider icons and colors
const providerConfig = {
  aws: {
    name: "AWS",
    color: "#FF9900",
    logo: "/inte/AWS.png"
  },
  gcp: {
    name: "GCP",
    color: "#4285F4",
    logo: "/inte/GCP.png"
  },
  azure: {
    name: "Azure",
    color: "#0078D4",
    logo: "/inte/Azure.png"
  }
};

const IamScanTableShimmer = ({ rows = 10 }) => {
  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Account Name</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Provider</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Schedule Status</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Current Interval</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Next Scan</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-800">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: rows }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                        <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="w-20 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="p-3">
                      <div className="w-24 h-6 bg-gray-200 rounded-full"></div>
                    </td>
                    <td className="p-3">
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="p-3">
                      <div className="w-24 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination shimmer */}
        <div className="flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="flex gap-1">
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ErrorState = ({ error, onRetry }) => (
  <Card>
    <CardContent className="text-center py-8">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-8 h-8 text-red-500" />
      </div>
      <p className="text-gray-900 font-semibold mb-4">{error}</p>
      <Button
        onClick={onRetry}
        className="bg-[#44087D] text-white hover:bg-[#5a0a9a]"
      >
        <RefreshCcw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

function IamScanTable() {
  const { showToast } = useToaster();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'onboarding'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const [pagination, setPagination] = useState({
    has_next: false,
    has_previous: false,
    last_page: 1,
    next_page: 0,
    page: 1,
    per_page: 10,
    prev_page: 0,
    total_count: 0,
  });

  const fetchSchedules = async (page = 1, perPage = 10, provider = null) => {
    try {
      setLoading(true);
      setError(null);

      const allData = cloudInfraScanSchedules.data || [];
      const providerLower = provider?.toLowerCase();

      let filtered = allData;
      if (providerLower) {
        filtered = allData.filter(
          (item) => (item.provider || "").toLowerCase() === providerLower
        );
      }

      const total = filtered.length;
      const lastPage = Math.max(1, Math.ceil(total / perPage));
      const currentPage = Math.min(Math.max(page, 1), lastPage);
      const startIndex = (currentPage - 1) * perPage;
      const pageItems = filtered.slice(startIndex, startIndex + perPage);

      setSchedules(pageItems);
      setPagination({
        has_next: currentPage < lastPage,
        has_previous: currentPage > 1,
        last_page: lastPage,
        next_page: currentPage < lastPage ? currentPage + 1 : 0,
        page: currentPage,
        per_page: perPage,
        prev_page: currentPage > 1 ? currentPage - 1 : 0,
        total_count: total,
      });
    } catch (err) {
      console.error("Error loading IAM schedules from mock data:", err);
      setSchedules([]);
      setError("Failed to load IAM schedules");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      fetchSchedules(1, pagination.per_page, selectedProvider);
      return;
    }
    
    setIsSearching(true);
    try {
      const allData = cloudInfraScanSchedules.data || [];
      const q = query.toLowerCase();
      const providerLower = selectedProvider?.toLowerCase();

      const filtered = allData.filter((item) => {
        if (
          providerLower &&
          (item.provider || "").toLowerCase() !== providerLower
        ) {
          return false;
        }
        const name = (item.accountName || "").toLowerCase();
        const id = (item.accountId || "").toLowerCase();
        return name.includes(q) || id.includes(q);
      });

      setSchedules(filtered);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total_count: filtered.length,
        last_page: 1,
        has_next: false,
        has_previous: false,
      }));
    } catch (e) {
      console.error("Search failed (mock)", e);
    } finally {
      setIsSearching(false);
    }

  }, [pagination.per_page, selectedProvider]);

  // Consolidated effect to handle fetching and searching without overlap
  useEffect(() => {
    if (viewMode !== 'list') return;

    if (searchQuery.trim()) {
      const debounceTimer = setTimeout(() => {
        handleSearch(searchQuery);
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      // Fetch default list when search is empty (handles mount, provider change, search clear)
      fetchSchedules(1, pagination.per_page, selectedProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedProvider, searchQuery, pagination.per_page, handleSearch]);

  const handleCreateSuccess = () => {
    setViewMode('list');
    fetchSchedules(1, pagination.per_page, selectedProvider);
  };

  const handleDeleteSchedule = (schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete?.id) return;
    // Mock delete: just remove locally
    setSchedules((prev) => prev.filter((s) => s.id !== scheduleToDelete.id));
    showToast("Schedule deleted (mock)", "success");
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  const generatePaginationButtons = () => {
    const buttons = [];
    const { last_page } = pagination;
    // Ensure we use the correct field from pagination state (repoSelectTable uses page/last_page)
    // Adjust if API returns current_page/total_pages
    
    const currentPage = pagination.page || pagination.current_page || 1;
    const lastPage = pagination.last_page || pagination.total_pages || 1;

    buttons.push(
      <button
        key={1}
        onClick={() => fetchSchedules(1, pagination.per_page, selectedProvider)}
        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
          currentPage === 1 ? "bg-[#44087D] text-white" : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
        }`}
      >
        1
      </button>
    );

    if (currentPage > 3) {
      buttons.push(<span key="ellipsis-start" className="px-3 py-2 text-sm text-gray-600">...</span>);
    }

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(lastPage - 1, currentPage + 1); i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => fetchSchedules(i, pagination.per_page, selectedProvider)}
          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
            currentPage === i ? "bg-[#44087D] text-white" : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
          }`}
        >
          {i}
        </button>
      );
    }

    if (currentPage < lastPage - 2) {
      buttons.push(<span key="ellipsis-end" className="px-3 py-2 text-sm text-gray-600">...</span>);
    }

    if (lastPage > 1) {
      buttons.push(
        <button
          key={lastPage}
          onClick={() => fetchSchedules(lastPage, pagination.per_page, selectedProvider)}
          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
            currentPage === lastPage ? "bg-[#44087D] text-white" : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
          }`}
        >
          {lastPage}
        </button>
      );
    }

    return buttons;
  };

  const getProviderLogo = (provider) => {
    const key = (provider || '').toLowerCase();
    return providerConfig[key]?.logo || null;
  };

  const formatProviderName = (provider) => {
    const key = (provider || '').toLowerCase();
    return providerConfig[key]?.name || provider || 'Unknown';
  };

  const convertInterval = (hours) => {
      if (!hours) return '-';
      if (hours >= 24 && hours % 24 === 0) return `${hours/24}d`;
      return `${hours}h`;
  };

  const formatNextScan = (schedule) => {
    if (schedule.next_scheduled_at) {
        return new Date(schedule.next_scheduled_at).toLocaleString();
    }
    // Fallback if next_scheduled_at is missing but we have start_hour
    if (schedule.start_hour) {
        return new Date(schedule.start_hour).toLocaleString();
    }
    return '-';
  };

  if (viewMode === 'onboarding') {
    return <IamOnboarding onBack={() => setViewMode('list')} onSuccess={handleCreateSuccess} />;
  }

  // Calculate total count (mock or from stats if available, else just total from pagination)
  const totalCount = pagination.total_count || schedules.length;

  return (
    <div>
       <CardContent className="p-0">
          {/* Action Bar / Search */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
             <div className="relative flex-1 w-full md:max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search schedules..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-[#44087D] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
             </div>
             
             <Button
                onClick={() => setViewMode('onboarding')}
                className="bg-[#44087D] text-white hover:bg-[#5a0a9a] whitespace-nowrap"
             >
               <RefreshCcw className="w-4 h-4 mr-2" />
               New Scan
             </Button>
          </div>

          {/* Filter Badges */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setSelectedProvider(null)}
                className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  selectedProvider === null
                    ? 'bg-[#44087D] text-white'
                    : 'bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white'
                }`}
              >
                All
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedProvider === null 
                    ? 'bg-white/20 text-white' 
                    : 'bg-[#E0D9F0] text-[#5D5275]'
                }`}>
                  {totalCount}
                </span>
              </button>

              {Object.keys(providerConfig).map((key) => {
                 // For now, count is not available per provider unless we fetch it. 
                 // We'll just show the badge without count or 0.
                 const config = providerConfig[key];
                 return (
                  <button
                    key={key}
                    onClick={() => setSelectedProvider(key)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 capitalize ${
                      selectedProvider === key
                        ? 'bg-[#44087D] text-white'
                        : 'bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white'
                    }`}
                  >
                    <img src={config.logo} alt={config.name} className="w-5 h-5 mr-2 object-contain" />
                    <span className="mr-1">{config.name}</span>
                  </button>
                 );
              })}
            </div>
          </div>

          {/* Table */}
          {loading ? (
             <IamScanTableShimmer rows={5} />
          ) : error ? (
             <ErrorState error={error} onRetry={() => fetchSchedules(pagination.page, pagination.per_page, selectedProvider)} />
          ) : (
            <div className="overflow-x-auto">
              <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-gray-300">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Account Name</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Provider</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Schedule Status</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Current Interval</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Next Scan</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-800">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {schedules.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="p-8 text-center text-gray-500">
                                <div className="flex flex-col items-center justify-center">
                                    <Shield className="w-12 h-12 text-gray-300 mb-2" />
                                    <p>No schedules found.</p>
                                </div>
                            </td>
                        </tr>
                    ) : (
                      schedules.map((schedule) => (
                        <tr key={schedule.id || schedule.scan_id} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                                 {/* Fallback icon if no logo */}
                                 <Cloud className="w-4 h-4 text-gray-500" />
                              </div>
                              <div>
                                  <div className="font-medium text-gray-900">{schedule.storage_account_name || schedule.account_name || schedule.name || 'Unnamed Account'}</div>
                                  <div className="text-xs text-gray-500">{schedule.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                             <div className="flex items-center gap-2">
                                {getProviderLogo(schedule.provider) && (
                                   <img src={getProviderLogo(schedule.provider)} alt={schedule.provider} className="w-4 h-4" />
                                )}
                                <span className="text-sm text-gray-600 capitalize">{formatProviderName(schedule.provider)}</span>
                             </div>
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                schedule.active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                            }`}>
                              {schedule.active !== false ? "Critical" : "Paused"}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                             Every {convertInterval(schedule.repeat_interval_h || schedule.repeatIntervalH)}
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                             {formatNextScan(schedule)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                               <button
                                 onClick={() => handleDeleteSchedule(schedule)}
                                 className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                 title="Delete schedule"
                               >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                 </svg>
                               </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
       </CardContent>

       {/* Pagination */}
       <div className="flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => fetchSchedules(pagination.prev_page || Math.max(1, (pagination.page || 1) - 1), pagination.per_page, selectedProvider)}
            disabled={!pagination.has_previous}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
              !pagination.has_previous
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
            }`}
          >
            Previous
          </Button>
          {generatePaginationButtons()}
          <Button
            onClick={() => fetchSchedules(pagination.next_page || ((pagination.page || 1) + 1), pagination.per_page, selectedProvider)}
            disabled={!pagination.has_next}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
              !pagination.has_next
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
            }`}
          >
            Next
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Per page:</label>
          <select
            value={pagination.per_page}
            onChange={(e) => fetchSchedules(1, Number(e.target.value), selectedProvider)}
            className="px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#44087D] focus:outline-none"
          >
            {[10, 20, 30, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scan schedule?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSchedule}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default IamScanTable;